import { useState } from "react";
import Sidebar from "@/components/Sidebar";
import AIChat from "@/components/AIChat";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Headphones, MessageSquare, Phone, Mail, Clock,
  HelpCircle, FileText, Video, Send, Sparkles
} from "lucide-react";

const FAQ_ITEMS = [
  {
    question: "What are your service hours?",
    answer: "Our service department is open Monday-Friday 7AM-6PM, Saturday 8AM-4PM. Emergency services are available 24/7."
  },
  {
    question: "How do I schedule a test drive?",
    answer: "You can schedule a test drive through our website, by calling our sales team, or walking into our showroom."
  },
  {
    question: "What financing options are available?",
    answer: "We offer various financing options including traditional loans, leasing, and special manufacturer incentives."
  },
  {
    question: "Do you accept trade-ins?",
    answer: "Yes! We accept trade-ins of all makes and models. Our team will provide a competitive appraisal."
  },
];

const CONTACT_OPTIONS = [
  { icon: Phone, label: "Call Us", value: "(555) 123-4567", action: "tel:5551234567" },
  { icon: Mail, label: "Email", value: "support@halcyonauto.com", action: "mailto:support@halcyonauto.com" },
  { icon: Clock, label: "Hours", value: "Mon-Sat: 8AM-8PM", action: null },
];

export default function SupportPage() {
  const [showChat, setShowChat] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitInquiry = async (e) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success("Your inquiry has been submitted! We'll get back to you soon.");
    setName("");
    setEmail("");
    setMessage("");
    setSubmitting(false);
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      
      <main className="dashboard-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground" data-testid="support-title">
              Customer Support
            </h1>
            <p className="text-muted-foreground">Get help with our AI assistant or contact our team</p>
          </div>
          <Button 
            className="btn-ai" 
            onClick={() => setShowChat(true)}
            data-testid="support-ai-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Chat with AI Support
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* AI Support Highlight */}
          <Card className="lg:col-span-2 overflow-hidden" data-testid="ai-support-card">
            <div className="bg-gradient-to-r from-blue-600 to-teal-500 p-6 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="font-display text-2xl font-bold">AI Support Agent</h2>
                  <p className="text-white/80">Available 24/7 to assist you</p>
                </div>
              </div>
              <p className="text-white/90 mb-4">
                Our AI-powered support agent can help you with questions about sales, service, 
                financing, parts, and more. Get instant answers anytime!
              </p>
              <Button 
                variant="secondary" 
                className="bg-white text-blue-600 hover:bg-white/90"
                onClick={() => setShowChat(true)}
                data-testid="start-ai-chat-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Start Conversation
              </Button>
            </div>
            <CardContent className="p-6">
              <h3 className="font-semibold text-foreground mb-4">What can the AI help with?</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { icon: Headphones, title: "General Inquiries", desc: "Hours, location, services" },
                  { icon: FileText, title: "Documentation", desc: "Forms, paperwork guidance" },
                  { icon: HelpCircle, title: "Product Questions", desc: "Vehicle specs, features" },
                  { icon: Video, title: "Tutorials", desc: "How-to guides and tips" },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{item.title}</div>
                      <div className="text-sm text-muted-foreground">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Contact Options */}
          <div className="space-y-4">
            <Card data-testid="contact-options">
              <CardHeader>
                <CardTitle>Contact Us</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {CONTACT_OPTIONS.map((option, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 ${option.action ? 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800' : ''}`}
                    onClick={() => option.action && window.open(option.action)}
                    data-testid={`contact-${idx}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <option.icon className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">{option.label}</div>
                      <div className="font-medium text-foreground">{option.value}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick Inquiry Form */}
            <Card data-testid="inquiry-form">
              <CardHeader>
                <CardTitle>Send Inquiry</CardTitle>
                <CardDescription>We'll respond within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitInquiry} className="space-y-4">
                  <Input
                    placeholder="Your Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="inquiry-name"
                  />
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="inquiry-email"
                  />
                  <Textarea
                    placeholder="How can we help?"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    data-testid="inquiry-message"
                  />
                  <Button 
                    type="submit" 
                    className="w-full btn-ai" 
                    disabled={submitting}
                    data-testid="submit-inquiry-btn"
                  >
                    {submitting ? "Sending..." : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-8">
          <h2 className="font-display text-xl font-semibold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {FAQ_ITEMS.map((faq, idx) => (
              <Card key={idx} data-testid={`faq-${idx}`}>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-blue-500" />
                    {faq.question}
                  </h3>
                  <p className="text-sm text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>

      <AIChat isOpen={showChat} onClose={() => setShowChat(false)} vertical="support" />
      
      {!showChat && (
        <button
          onClick={() => setShowChat(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full btn-ai flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-40"
          data-testid="support-chat-toggle"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
